import { useState, useEffect } from 'react';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { FileSignature, Loader2, ExternalLink, RefreshCw } from 'lucide-react';

type Pendente = {
  tipo: 'plataforma' | 'fotovoltaico';
  documentId: string;
  signerIdEmpresa?: string;
  contratoId?: string;
  titulo: string;
};

export default function AssinaturasPendentesSun7() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [pendentes, setPendentes] = useState<Pendente[]>([]);
  const [assinandoId, setAssinandoId] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/assinafy/pendentes-assinatura-empresa');
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Erro ${res.status}`);
      }
      const data = await res.json();
      setPendentes(data.pendentes || []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao carregar';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
      setPendentes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const abrirLinkAssinatura = async (item: Pendente) => {
    setAssinandoId(item.documentId);
    try {
      const params = new URLSearchParams({ documentId: item.documentId });
      if (item.signerIdEmpresa) params.set('signerIdEmpresa', item.signerIdEmpresa);
      const res = await fetchWithAuth(`/api/assinafy/link-assinatura-empresa?${params}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Erro ${res.status}`);
      }
      const data = await res.json();
      if (data.signingUrlEmpresa) {
        window.open(data.signingUrlEmpresa, '_blank', 'noopener,noreferrer');
        toast({ title: 'Link aberto', description: 'Assine o documento na aba aberta. Após assinar, a lista será atualizada quando ambos (cliente/parceiro e Sun7) tiverem assinado.' });
      } else {
        throw new Error('Link não retornado');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao obter link';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setAssinandoId(null);
    }
  };

  const sincronizarContratosParceiros = async () => {
    setSincronizando(true);
    try {
      const res = await fetchWithAuth('/api/assinafy/sincronizar-contratos-plataforma-parceiros', { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Erro ${res.status}`);
      }
      const data = await res.json();
      const n = data.adicionados ?? 0;
      toast({
        title: 'Sincronização concluída',
        description: n > 0
          ? `${n} contrato(s) adicionado(s) à aba Documentos dos parceiros.`
          : (data.message || 'Nenhum contrato novo para adicionar.'),
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao sincronizar';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setSincronizando(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Assinaturas Pendentes</h1>
        <p className="text-muted-foreground mt-1">
          Contratos que aguardam a assinatura da Sun7. Clique em &quot;Assinar como Sun7&quot; para abrir o link no Assinafy.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5" />
            Documentos aguardando assinatura da empresa
          </CardTitle>
          <CardDescription>
            Contratos de plataforma (parceiros/vendedores) e contratos fotovoltaicos (clientes) com dois signatários. O documento só fica concluído quando cliente/parceiro e Sun7 assinarem.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : pendentes.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Nenhum documento aguardando assinatura da Sun7.</p>
          ) : (
            <ul className="space-y-3">
              {pendentes.map((item) => (
                <li
                  key={`${item.tipo}-${item.documentId}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 bg-card"
                >
                  <div className="min-w-0">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {item.tipo === 'plataforma' ? 'Contrato plataforma' : 'Contrato fotovoltaico'}
                    </span>
                    <p className="font-medium truncate">{item.titulo}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => abrirLinkAssinatura(item)}
                    disabled={assinandoId === item.documentId}
                  >
                    {assinandoId === item.documentId ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <ExternalLink className="h-4 w-4 mr-2" />
                    )}
                    Assinar como Sun7
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {!loading && pendentes.length > 0 && (
            <Button variant="outline" className="mt-4" onClick={carregar}>
              Atualizar lista
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Parceiros existentes
          </CardTitle>
          <CardDescription>
            Para parceiros que assinaram o contrato de plataforma antes da implementação do salvamento automático, o contrato não foi salvo na aba Documentos. Use o botão abaixo para sincronizar os contratos já assinados no Assinafy com a aba Documentos de cada parceiro.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={sincronizarContratosParceiros}
            disabled={sincronizando}
          >
            {sincronizando ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sincronizando...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sincronizar contratos na aba Documentos
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
